import { faMicrochip, faRepeat } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactElement } from 'react';

import HowItWorks from '@/assets/HowItsWork-2048x1910.webp';
import JobMatchingImage from '@/assets/Jobmatching-scaled.webp';
import PeopleJobMatching from '@/assets/PeopleJobMatching-2048x1543.webp';
import PersonJobMatching from '@/assets/PersonJobMatching-2048x1198.webp';
import Table from '@/assets/Table2-2048x769.webp';
import Button from '@/components/Button';
import Layout from '@/components/Landing/Layout';
import Text from '@/components/Text';
import { createMetaTags } from '@/utils/seo';

const JobMatching = () => {
  return (
    <>
      <Head>
        {createMetaTags({
          description:
            'AI-powered job matching for recruiters. Paste a job offer, set criteria, and get ranked candidate ratings instantly. Automates 70% of repetitive HR tasks.',
          title: 'Job Matching — AI Candidate Ranking | MindPal',
        })}
      </Head>
      <div>
        <div className="relative flex flex-col items-center justify-center gap-10 overflow-hidden p-5 pt-40">
          <Text
            variant="display-2"
            className="text-center text-h1 md:text-display-2"
            as="h1"
          >
            Job Matching
          </Text>
          <Text
            variant="h3"
            as="h2"
            className="whitespace-pre-wrap text-center text-h5 md:text-h3"
          >
            Are you looking for the perfect candidate? {'\n'} You no longer have
            to spend hours searching databases. {'\n'}Just use our JobMatching.
          </Text>
          <a href="https://app.mindpal.co/dashboard">
            <Button tabIndex={-1}>Try now</Button>
          </a>
          <div className="pointer-events-none absolute right-0 top-0 w-[70vw] animate-move-in-fly ">
            <div className="absolute right-[-22vw] top-[-310px] h-[1000px] w-[1000px] bg-gradient-radial from-[#885bff80] to-transparent to-70%" />
          </div>
          <div className="pointer-events-none absolute bottom-[146px] left-[-30vw] w-[70vw] animate-move-in-fly ">
            <div className="absolute right-[-22vw] top-[-310px] h-[1000px] w-[1000px] bg-gradient-radial from-[#885bff80] to-transparent to-70%" />
          </div>
          <Image className="w-full max-w-6xl" src={JobMatchingImage} alt="" />
        </div>
        <div className="relative flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute -bottom-32 -z-10 h-[850px] w-[850px] bg-gradient-radial from-[#885bff80] to-transparent to-70%" />
          <Image
            className="w-full max-w-6xl pt-20"
            src={PersonJobMatching}
            alt=""
          />
        </div>
        <div className="flex flex-col items-center justify-center bg-neutral-0 bg-grid bg-cover bg-repeat p-5 text-neutral-1000 md:p-20">
          <div className="flex w-full max-w-7xl flex-col gap-5">
            <Text variant="display-2" className="!font-semibold">
              Today, 70%+
            </Text>
            <Text variant="h3">
              recruiter&apos;s work involves{' '}
              <FontAwesomeIcon className="text-primary-600" icon={faRepeat} />{' '}
              <span className="text-primary-600">repetitive activities.</span>
            </Text>
            <Text variant="h3">
              <FontAwesomeIcon
                className="text-primary-600"
                icon={faMicrochip}
              />
              <span className="text-primary-600"> AI</span> helps
            </Text>
            <Text variant="display-2" className="!font-bold">
              reducing them to zero.{' '}
            </Text>
          </div>
        </div>
        <div className="relative flex flex-col items-center overflow-hidden p-5 md:p-20">
          <div className="pointer-events-none absolute right-0 top-0 w-[70vw] animate-move-in-fly ">
            <div className="absolute right-[-22vw] top-[-310px] h-[1000px] w-[1000px] bg-gradient-radial from-[#885bff80] to-transparent to-70%" />
          </div>
          <div className="flex max-w-6xl flex-col items-center gap-5">
            <Text variant="display-3" className="text-center" as="h2">
              How it&apos;s working?{' '}
            </Text>
            <Text
              variant="h4"
              className="whitespace-pre-wrap text-center !text-body md:!text-h4"
            >
              Paste the job offer → Set your criteria → JobMatching will show
              you the ratings of the best{'\n'} candidates.→ Verify the facts →
              Contact the best → Enjoy the time you save.{' '}
            </Text>
            <Link href="/dashboard">
              <Button tabIndex={-1} className="w-fit self-center">
                Try now
              </Button>
            </Link>
            <div className="flex w-full">
              <Image
                className="w-1/2 object-contain"
                src={PeopleJobMatching}
                alt=""
              />
              <Image className="w-1/2 object-contain" src={HowItWorks} alt="" />
            </div>
            <Image src={Table} alt="" />
          </div>
        </div>
      </div>
    </>
  );
};

export default JobMatching;

JobMatching.getLayout = (page: ReactElement) => <Layout>{page}</Layout>;
